import { functions } from "../firebase";

// Client wrappers for the first-party Meta callables (functions/src/
// metaPublishing.ts). Mirrors tiktokConnection.ts / linkedinConnection.ts:
// tokens NEVER cross this boundary — the connection read returns only display
// chrome, and publishing is done entirely server-side.

export type MetaConnection = {
  connected: boolean;
  platform?: "instagram" | "facebook";
  pageName?: string;
  igUsername?: string;
  igUserId?: string;
  scopes?: string[];
  tokenValid?: boolean;
};

export type MetaPublishResult = {
  ok: boolean;
  igMediaId?: string;
  id?: string;
};

// Read the community's Meta connection state for the connect panel.
export const getMetaConnection = async (
  communityId: string
): Promise<MetaConnection> => {
  const callable = functions.httpsCallable("metaGetConnection");
  const res = await callable({ communityId });
  return res.data as MetaConnection;
};

// Start OAuth: returns the authorize URL to open in a popup. platform picks the
// Facebook login (Page + linked IG) or the Instagram Business login.
export const startMetaAuth = async (
  communityId: string,
  platform: "instagram" | "facebook" = "facebook"
): Promise<{ url: string }> => {
  const callable = functions.httpsCallable("metaStartAuth");
  const res = await callable({ communityId, platform });
  return res.data as { url: string };
};

// Finish OAuth: the /auth/meta/callback route calls this with the ?code and
// ?state Meta returned.
export const completeMetaAuth = async (params: {
  code: string;
  state: string;
}): Promise<MetaConnection> => {
  const callable = functions.httpsCallable("metaCompleteAuth");
  const res = await callable(params);
  return res.data as MetaConnection;
};

// Publish a project (or explicit media) to Instagram.
export const publishToInstagram = async (params: {
  communityId: string;
  projectId?: string;
  mediaUrls?: string[];
  caption?: string;
  mediaType?: "IMAGE" | "REELS";
}): Promise<MetaPublishResult> => {
  const callable = functions.httpsCallable("metaPublishToInstagram", {
    timeout: 120000,
  });
  const res = await callable(params);
  return res.data as MetaPublishResult;
};

// Publish to the coach's Facebook Page.
export const publishToFacebookPage = async (params: {
  communityId: string;
  message?: string;
  imageUrl?: string;
  videoUrl?: string;
  link?: string;
}): Promise<MetaPublishResult> => {
  const callable = functions.httpsCallable("metaPublishToPage", {
    timeout: 120000,
  });
  const res = await callable(params);
  return res.data as MetaPublishResult;
};

// Clear the community's Meta connection.
export const disconnectMeta = async (
  communityId: string
): Promise<{ connected: boolean }> => {
  const callable = functions.httpsCallable("metaDisconnect");
  const res = await callable({ communityId });
  return res.data as { connected: boolean };
};
